import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowDownRight, FiArrowUpRight, FiExternalLink, FiMail } = FiIcons;

const modes = [
  {
    index: '01',
    title: 'Clarify',
    description: 'Make the important thing legible: the opportunity, the constraint, and the next honest move.',
    label: 'Strategy / Direction'
  },
  {
    index: '02',
    title: 'Build',
    description: 'Turn good thinking into useful systems, products, stories, and ways of working that can hold up over time.',
    label: 'Technology / Media'
  },
  {
    index: '03',
    title: 'Steward',
    description: 'Stay close to the work as it grows, changes shape, or asks for a different kind of attention.',
    label: 'Operations / Ventures'
  }
];

const principles = [
  'Independent by design',
  'Selective by nature',
  'Durable by default'
];

const rotatingHeadlines = [
  'between categories.',
  'that clarifies.',
  'that takes shape.',
  'that connects.',
  'that lasts.'
];

const TypewriterHeadline = () => {
  const shouldReduceMotion = useReducedMotion();
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState(rotatingHeadlines[0]);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (shouldReduceMotion) {
      setDisplayedText(rotatingHeadlines[0]);
      return undefined;
    }

    const targetText = rotatingHeadlines[phraseIndex];
    let timer;

    if (!isDeleting && displayedText === targetText) {
      timer = setTimeout(() => setIsDeleting(true), 2200);
    } else if (isDeleting && displayedText.length === 0) {
      timer = setTimeout(() => {
        setIsDeleting(false);
        setPhraseIndex((currentIndex) => (currentIndex + 1) % rotatingHeadlines.length);
      }, 260);
    } else {
      timer = setTimeout(() => {
        setDisplayedText((currentText) => (
          isDeleting
            ? currentText.slice(0, -1)
            : targetText.slice(0, currentText.length + 1)
        ));
      }, isDeleting ? 42 : 88);
    }

    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, phraseIndex, shouldReduceMotion]);

  return (
    <span className="studio-typewriter" aria-hidden="true">
      {displayedText}
      {!shouldReduceMotion && <span className="studio-typewriter-caret" />}
    </span>
  );
};

const ConstellationField = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <div className="absolute -right-40 top-24 h-[32rem] w-[32rem] rounded-full border border-[#d49a55]/20 sm:h-[46rem] sm:w-[46rem]" />
    <div className="absolute -right-20 top-44 h-[24rem] w-[24rem] rounded-full border border-[#d49a55]/10 sm:h-[36rem] sm:w-[36rem]" />
    <svg
      className="absolute inset-0 h-full w-full opacity-80"
      viewBox="0 0 1200 820"
      preserveAspectRatio="none"
      fill="none"
    >
      <path d="M650 120L850 270L1090 180L980 510L1160 700" stroke="currentColor" className="text-[#d49a55]/30" />
      <path d="M850 270L720 560L980 510" stroke="currentColor" className="text-[#f5f0e8]/15" />
      <path d="M720 560L430 690L180 520" stroke="currentColor" className="text-[#d49a55]/20" />
      <path d="M180 520L330 250L650 120" stroke="currentColor" className="text-[#f5f0e8]/10" />
      <circle cx="650" cy="120" r="5" className="fill-[#f5f0e8]" />
      <circle cx="850" cy="270" r="7" className="fill-[#d49a55] studio-node-pulse" />
      <circle cx="1090" cy="180" r="4" className="fill-[#f5f0e8]" />
      <circle cx="980" cy="510" r="5" className="fill-[#d49a55]" />
      <circle cx="720" cy="560" r="4" className="fill-[#f5f0e8]" />
      <circle cx="430" cy="690" r="6" className="fill-[#d49a55] studio-node-pulse" />
      <circle cx="180" cy="520" r="4" className="fill-[#f5f0e8]" />
      <circle cx="330" cy="250" r="5" className="fill-[#d49a55]" />
    </svg>
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_25%,rgba(212,154,85,0.16),transparent_28%),linear-gradient(115deg,transparent_20%,rgba(11,17,18,0.2)_55%,rgba(11,17,18,0.95)_100%)]" />
  </div>
);

const Home = () => (
  <div className="studio-page overflow-hidden bg-[#f4efe5] text-[#172022]">
    <section id="top" className="relative isolate flex min-h-[min(920px,100vh)] items-end overflow-hidden bg-[#0b1112] text-[#f5f0e8]">
      <ConstellationField />

      <div className="relative z-10 mx-auto flex w-full max-w-[1440px] items-end px-6 pb-16 pt-40 sm:px-10 sm:pb-20 lg:px-16 lg:pb-24">
        <div className="max-w-5xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="studio-kicker text-[#d49a55]"
          >
            Celestia Fortis / Independent practice
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.08 }}
            aria-label="The work between categories."
            className="mt-7 max-w-5xl font-serif text-[clamp(3.5rem,8vw,8.5rem)] font-medium leading-[0.9] tracking-[-0.055em]"
          >
            The work{' '}
            <TypewriterHeadline />
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-8 grid max-w-3xl gap-8 md:grid-cols-[1.25fr_0.75fr] md:items-end"
          >
            <p className="max-w-2xl text-xl font-light leading-relaxed text-[#d7d4cb] sm:text-2xl">
              A private studio for building durable systems, exploring new ideas, and stewarding meaningful work across technology, media, and ventures.
            </p>
            <p className="studio-meta text-[#a6aaa5]">
              Independent / Selective / Long horizon
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href="#constellation" className="studio-button studio-button-primary">
              Enter the constellation
              <SafeIcon icon={FiArrowDownRight} className="h-4 w-4" aria-hidden="true" />
            </a>
            <Link to="/book" className="studio-button studio-button-ghost">
              Start a conversation
              <SafeIcon icon={FiArrowUpRight} className="h-4 w-4" aria-hidden="true" />
            </Link>
          </motion.div>

          <div className="mt-20 grid max-w-3xl grid-cols-2 gap-y-4 border-t border-[#f5f0e8]/20 pt-5 text-[#a6aaa5] sm:grid-cols-4 sm:gap-4">
            {['Strategy', 'Technology', 'Media', 'Ventures'].map((item, index) => (
              <div key={item} className="studio-meta flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#d49a55]" />
                <span>{String(index + 1).padStart(2, '0')} / {item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section id="constellation" className="relative bg-[#f4efe5] px-6 py-28 sm:px-10 sm:py-36 lg:px-16">
      <div className="mx-auto max-w-[1440px]">
        <div className="grid gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:gap-24">
          <div>
            <p className="studio-kicker text-[#9b6a36]">01 / The studio</p>
            <h2 className="mt-6 max-w-md font-serif text-5xl font-medium leading-[0.98] tracking-[-0.04em] text-[#172022] sm:text-6xl">
              A constellation, not a catalog.
            </h2>
          </div>
          <div className="max-w-2xl lg:pt-12">
            <p className="text-2xl font-light leading-relaxed text-[#3d4845] sm:text-3xl">
              Celestia Fortis is the shared foundation beneath different kinds of work. The shape changes with the question; the standard stays the same.
            </p>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#68716b]">
              There is no fixed menu of services here. Some seasons call for a clear decision. Others call for a product, a system, a story, or a patient hand at the wheel.
            </p>
          </div>
        </div>

        <div className="mt-20 grid gap-5 md:grid-cols-3">
          {modes.map((mode, index) => (
            <motion.article
              key={mode.index}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: index * 0.08 }}
              className="studio-card group"
            >
              <div className="flex items-start justify-between gap-4">
                <span className="studio-kicker text-[#9b6a36]">{mode.index}</span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#d49a55] transition-transform duration-300 group-hover:scale-150" />
              </div>
              <h3 className="mt-16 font-serif text-4xl font-medium tracking-[-0.035em] text-[#172022]">{mode.title}</h3>
              <p className="mt-5 text-lg leading-relaxed text-[#59635e]">{mode.description}</p>
              <p className="studio-meta mt-10 border-t border-[#172022]/15 pt-4 text-[#8b6c4c]">{mode.label}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>

    <section id="approach" className="bg-[#172022] px-6 py-28 text-[#f5f0e8] sm:px-10 sm:py-36 lg:px-16">
      <div className="mx-auto grid max-w-[1440px] gap-16 lg:grid-cols-[0.8fr_1.2fr] lg:gap-28">
        <div>
          <p className="studio-kicker text-[#d49a55]">02 / The approach</p>
          <h2 className="mt-6 max-w-lg font-serif text-5xl font-medium leading-[0.98] tracking-[-0.04em] sm:text-6xl">
            One studio. Different orbits.
          </h2>
        </div>

        <div className="max-w-2xl">
          <p className="text-2xl font-light leading-relaxed text-[#d7d4cb] sm:text-3xl">
            The best work is rarely one discipline all the way through. It moves between the strategic, the technical, and the human.
          </p>
          <div className="mt-14 grid gap-4 border-t border-[#f5f0e8]/20 pt-5 sm:grid-cols-3">
            {principles.map((principle, index) => (
              <div key={principle} className="border-b border-[#f5f0e8]/20 pb-5 sm:border-b-0 sm:border-r sm:pr-5 last:border-r-0">
                <span className="studio-kicker text-[#8e9790]">0{index + 1}</span>
                <p className="mt-4 text-xl text-[#f5f0e8]">{principle}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section id="work" className="bg-[#e8dfd1] px-6 py-28 sm:px-10 sm:py-36 lg:px-16">
      <div className="mx-auto max-w-[1440px]">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <p className="studio-kicker text-[#9b6a36]">03 / Current orbits</p>
            <h2 className="mt-6 max-w-xl font-serif text-5xl font-medium leading-[0.98] tracking-[-0.04em] text-[#172022] sm:text-6xl">
              The public work has another home.
            </h2>
          </div>
          <p className="max-w-sm text-lg leading-relaxed text-[#59635e]">
            Celestia Fortis stays intentionally spacious. Follow the visible work, or get in touch about the work that is still taking shape.
          </p>
        </div>

        <div className="mt-20 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]">
          <a
            href="https://joshuacohen.net"
            target="_blank"
            rel="noreferrer"
            className="studio-orbit-card studio-orbit-card-featured group"
          >
            <span className="studio-kicker text-[#f0c082]">Public work / JoshuaCohen.net</span>
            <div className="mt-24 flex items-end justify-between gap-6">
              <div>
                <h3 className="font-serif text-4xl font-medium tracking-[-0.035em] text-[#f5f0e8] sm:text-5xl">Ideas, tools, and experiments.</h3>
                <p className="mt-5 max-w-lg text-lg leading-relaxed text-[#c6cbc4]">The personal brand, writing, and public-facing work live over there.</p>
              </div>
              <SafeIcon icon={FiExternalLink} className="mb-1 h-7 w-7 shrink-0 text-[#d49a55] transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" aria-hidden="true" />
            </div>
          </a>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
            <div className="studio-orbit-card studio-orbit-card-muted">
              <span className="studio-kicker text-[#9b6a36]">Private systems</span>
              <h3 className="mt-12 font-serif text-3xl font-medium tracking-[-0.03em] text-[#172022]">The work that supports the work.</h3>
              <p className="mt-4 text-lg leading-relaxed text-[#59635e]">Products, operations, and infrastructure that do not need to be public to be meaningful.</p>
            </div>
            <div className="studio-orbit-card studio-orbit-card-outline">
              <span className="studio-kicker text-[#9b6a36]">Future ventures</span>
              <h3 className="mt-12 font-serif text-3xl font-medium tracking-[-0.03em] text-[#172022]">Open space for the next orbit.</h3>
              <p className="mt-4 text-lg leading-relaxed text-[#59635e]">A place for aligned opportunities, collaborations, and ideas still finding their form.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="contact" className="relative overflow-hidden bg-[#0b1112] px-6 py-32 text-[#f5f0e8] sm:px-10 sm:py-44 lg:px-16">
      <div className="absolute -left-40 top-20 h-96 w-96 rounded-full border border-[#d49a55]/15" aria-hidden="true" />
      <div className="relative z-10 mx-auto max-w-[1440px]">
        <p className="studio-kicker text-[#d49a55]">04 / Start a conversation</p>
        <div className="mt-7 grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
          <h2 className="max-w-4xl font-serif text-6xl font-medium leading-[0.92] tracking-[-0.05em] sm:text-8xl">
            Have something worth building?
          </h2>
          <div>
            <p className="max-w-md text-xl font-light leading-relaxed text-[#d7d4cb]">
              Bring the question, the opportunity, or the unfinished thought. We can start with clarity.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/book" className="studio-button studio-button-primary">
                Book a conversation
                <SafeIcon icon={FiArrowUpRight} className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a href="mailto:hello@celestiafortis.com" className="studio-button studio-button-ghost">
                <SafeIcon icon={FiMail} className="h-4 w-4" aria-hidden="true" />
                Email the studio
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Home;
